import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Input, Button, Card, Typography, message, Pagination } from "antd";
import * as Yup from "yup";
import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";
import "antd/dist/reset.css";
import { useTranslation } from "react-i18next";
import HomeButton from "../components/HomeButton";

const { TextArea } = Input;
const { Title } = Typography;

const NERFormSchema = Yup.object().shape({
  inputText: Yup.string().required("Text input is required"),
});

export const NERPage = () => {
  const [entities, setEntities] = useState<{
    words: string[];
    classes: string[];
  } | null>(null);
  const [history, setHistory] = useState<
    { inputText: string; output: any; createdTime: string }[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const { t } = useTranslation();

  const getColor = (entity: string) => {
    switch (entity) {
      case "PERSON":
        return "font-bold text-blue-600";
      case "ART":
        return "font-bold text-green-600";
      case "NORP":
        return "font-bold text-purple-600";
      case "LANGUAGE":
        return "font-bold text-orange-600";
      case "ADAGE":
        return "font-bold text-red-600";
      case "CARDINAL":
        return "font-bold text-teal-600";
      case "CONTACT":
        return "font-bold text-pink-600";
      case "DATE":
        return "font-bold text-yellow-600";
      case "DISEASE":
        return "font-bold text-indigo-600";
      case "EVENT":
        return "font-bold text-gray-600";
      case "FACILITY":
        return "font-bold text-cyan-600";
      case "GPE":
        return "font-bold text-orange-500";
      case "LAW":
        return "font-bold text-lime-600";
      case "LOCATION":
        return "font-bold text-rose-600";
      case "MISCELLANEOUS":
        return "font-bold text-emerald-600";
      case "MONEY":
        return "font-bold text-fuchsia-600";
      case "NON_HUMAN":
        return "font-bold text-slate-600";
      case "ORDINAL":
        return "font-bold text-amber-600";
      case "ORGANISATION":
        return "font-bold text-rose-500";
      case "PERCENTAGE":
        return "font-bold text-violet-600";
      case "POSITION":
        return "font-bold text-coral-600";
      case "PRODUCT":
        return "font-bold text-olive-600";
      case "PROJECT":
        return "font-bold text-maroon-600";
      case "QUANTITY":
        return "font-bold text-teal-500";
      case "TIME":
        return "font-bold text-gray-400";
      default:
        return "text-gray-600";
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      // console.log(token);
      const response = await axios.get(`${API_BASE_URL}/zhadiger/api/ner`, {
        headers: {
          Authorization: `${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
        withCredentials: true,
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setHistory(
        data.map((item: any) => ({
          inputText: item.inputText,
          output: JSON.parse(item.output),
          createdTime: item.createdTime,
        }))
      );
      message.success("History fetched successfully!");
    } catch (error) {
      console.error("Error fetching history:", error);
      message.error("Error fetching history.");
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleFormSubmit = async (
    values: { inputText: string },
    { setSubmitting }: any
  ) => {
    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/zhadiger/api/ner`,
        values.inputText,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      setEntities(response.data);
      message.success("Entities recognized successfully!");
      fetchHistory();
    } catch (error) {
      console.error("Error processing request:", error);
      message.error("Error processing request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      <div className="bg-blue-50 min-h-screen">
        <div className="flex justify-between items-center mb-4 ml-4 pt-4 ">
          <HomeButton />
        </div>
        <div className="flex flex-row pl-6 pr-6 space-x-6 bg-blue-50">
          <div className="w-1/3 border-r border-gray-300">
            <Title level={4} className="text-center">
              {t("query_history")}
            </Title>
            <ul className="space-y-4">
              {paginatedHistory.map((entry, index) => {
                const words = entry.output?.words || [];
                const classes = entry.output?.classes || [];

                return (
                  <li key={index} className="p-4 bg-gray-100 rounded shadow">
                    <p className="font-medium text-gray-800">
                      {entry.inputText}
                    </p>

                    {words.length > 0 ? (
                      <div className="mt-2 overflow-auto">
                        {words.map((word: string, idx: number) => {
                          const entity = classes[idx];

                          return (
                            <span
                              key={idx}
                              className={`${getColor(entity)} mr-1`}
                            >
                              {word}
                              {entity ? (
                                <span className="text-gray-500 text-xs">
                                  {" "}
                                  ({entity})
                                </span>
                              ) : null}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">
                        No recognized entities.
                      </p>
                    )}
                    <p className="text-gray-500 text-xs mt-5">
                      {new Date(entry.createdTime).toLocaleString()}
                    </p>
                  </li>
                );
              })}
            </ul>
            <Pagination
              current={currentPage}
              pageSize={itemsPerPage}
              total={history.length}
              onChange={handlePageChange}
              className="mt-4 text-center"
            />
          </div>
          <div className="flex flex-col items-center w-2/3 space-y-6">
            <Title level={2} className="text-center">
              {t("named_entity_recognition")}
            </Title>

            <Card className="w-full max-w-[80%]">
              <Formik
                initialValues={{ inputText: "" }}
                validationSchema={NERFormSchema}
                onSubmit={handleFormSubmit}
              >
                {({ isSubmitting, handleSubmit, errors, touched }) => (
                  <Form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <Field name="inputText">
                        {({ field }: any) => (
                          <TextArea
                            {...field}
                            rows={6}
                            placeholder={t("enter_your_text")}
                            status={
                              errors.inputText && touched.inputText
                                ? "error"
                                : ""
                            }
                          />
                        )}
                      </Field>
                      {errors.inputText && touched.inputText && (
                        <div className="text-red-500">{errors.inputText}</div>
                      )}
                    </div>

                    <div className="flex justify-center">
                      <Button
                        className="max-w-[20%]"
                        type="primary"
                        htmlType="submit"
                        loading={isSubmitting}
                        block
                        disabled={isSubmitting}
                      >
                        {t("extract_entities")}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>

              {entities?.words && (
                <div className="mt-6">
                  <Title level={4}>{t("recognized_entities")}:</Title>
                  <div className="mt-2 space-y-1 text-xl overflow-auto">
                    {entities.words.map((word, index) => {
                      const currentClass = entities.classes[index];
                      return (
                        <span
                          key={index}
                          className={`${getColor(currentClass)} mr-1`}
                        >
                          {word}
                          {currentClass && (
                            <span className="text-gray-500">
                              {" "}
                              ({currentClass})
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default NERPage;
